import type { PvpRuneWireKind } from '@kenocyber/shared';

/**
 * O que uma runa é, quando desenhada.
 *
 * ## O que substituiu
 *
 * Até o gate V25B a runa não tinha arte: existia um `Phaser.GameObjects.Arc`
 * por pad, criado com a arena e apenas recolorido - escuro vazio, tingido
 * ocupado. O disco tinha exatamente o raio de coleta, o que dizia ao jogador
 * "esta área faz alguma coisa", que é a linguagem de um booster e não a de um
 * objeto que se pega. Ele foi removido, não coberto.
 *
 * ## Por que é dado, e não três funções de desenho
 *
 * Um renderer que consumisse três funções acabaria com `if (kind === ...)` no
 * meio de um arquivo que já desenha seis cascos e noventa tiros. Aqui existe
 * UMA forma - {@link PvpRuneAppearance} - e o compositor não sabe qual runa
 * está desenhando. Ele lê camadas.
 *
 * ## Por que é puro
 *
 * Sem Phaser, sem DOM, sem relógio, sem sorteio. Cada número que um quadro
 * precisa é função de `(spec, elapsedMs)`, o que permite testar a matemática de
 * quadro sem navegador e faz dois clientes que fotografam o mesmo instante
 * desenharem o mesmo quadro.
 *
 * ## O que ele NUNCA decide
 *
 * Que uma runa existe, qual é, quando foi pega, quando volta, o que ela faz e
 * quanto dura. Isso é `packages/game-domain/src/pvp/runes.ts`, e nada deste
 * arquivo alcança aquele.
 */

/* -------------------------------------------------------------------------- */
/*  Paleta                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Quatro degraus, do coração quase branco à sombra profunda.
 *
 * `core` é quase branco de propósito: um núcleo saturado lê como adesivo. As
 * camadas de volume tomam `hot` e `body`, e `deep` é o que faz a face não
 * iluminada de um sólido parecer realmente não iluminada.
 *
 * ## Por que é a fonte única da cor da runa
 *
 * `PVP_RUNE_TINT` deriva daqui (`scene-model.ts`), em vez de existir ao lado.
 * O artefato no pad e o sinal que a nave carrega enquanto está sob o efeito têm
 * de dizer a mesma coisa, e duas tabelas de cor acabariam discordando - foi
 * exatamente esse o risco registrado quando o laboratório desenhou Velocidade
 * em ciano sobre um pad que o produto pintava de verde.
 */
export interface PvpRunePalette {
  readonly core: number;
  readonly hot: number;
  readonly body: number;
  readonly deep: number;
}

/**
 * As três paletas aprovadas no gate V25A.
 *
 * Velocidade deixou de ser verde (`0x3cff8a`) e Teleporte deixou de ser ciano
 * (`0x3cd2ff`): a direção aprovada é âmbar, azul elétrico e violeta, e os dois
 * conceitos precisavam parar de dividir a família do ciano com a marca da
 * própria nave (`PVP_OWN_TINT`, `0x4ce0ff`). O azul de Velocidade é mais
 * saturado e mais escuro que aquela marca, e a forma - uma ponta, não um anel -
 * é o que separa os dois quando a cor não separa.
 */
export const PVP_RUNE_PALETTES: Readonly<Record<PvpRuneWireKind, PvpRunePalette>> = Object.freeze({
  double_damage: Object.freeze({ core: 0xfff2e2, hot: 0xff8a3d, body: 0xff5a3c, deep: 0x8e1c07 }),
  speed: Object.freeze({ core: 0xeafcff, hot: 0x7fe9ff, body: 0x2fb4ff, deep: 0x0b3f8e }),
  teleport: Object.freeze({ core: 0xf3ecff, hot: 0xb98cff, body: 0x7b3cff, deep: 0x241056 }),
});

/* -------------------------------------------------------------------------- */
/*  Vocabulário                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Os três níveis de decoração, e nada além disso.
 *
 * O LOD remove DECORAÇÃO. Nunca remove a runa, nunca muda qual runa é, nunca
 * esconde o tipo e nunca toca um sinal autoritativo - por isso `critical` ainda
 * desenha o núcleo e o selo, que são o que diz "há uma runa aqui e é aquela".
 */
export const PVP_RUNE_LOD_LEVELS = ['full', 'reduced', 'critical'] as const;
export type PvpRuneLod = (typeof PVP_RUNE_LOD_LEVELS)[number];

const LOD_RANK: Readonly<Record<PvpRuneLod, number>> = Object.freeze({
  critical: 0,
  reduced: 1,
  full: 2,
});

export function pvpRuneLodAtLeast(current: PvpRuneLod, required: PvpRuneLod): boolean {
  return LOD_RANK[current] >= LOD_RANK[required];
}

/**
 * As receitas que o produto assa, e nenhuma a mais.
 *
 * Nove. O laboratório do gate V25A tinha onze: duas delas serviam apenas a
 * propostas que não foram promovidas, e saíram junto com elas. Uma receita sem
 * consumidor é código morto com nome bonito.
 *
 * Os nomes das duas não são citados aqui de propósito. O lockdown recusa um
 * `dist` que os contenha, e hoje isso passa porque o minificador remove
 * comentários - uma garantia que depende de uma ferramenta em vez de depender
 * do código. `rune-appearance.test.ts` os nomeia, que é onde eles têm valor:
 * num teste que falha se algum voltar.
 */
export const PVP_RUNE_RECIPES = [
  /** Queda radial suave. O coração quase branco de todas as três. */
  'core-glow',
  /** Uma esfera com terminador: volume sem shader. */
  'orb-shaded',
  /** Um cristal facetado, iluminado do alto à esquerda. */
  'facet-shard',
  /** Uma lâmina, larga na base e pontuda na ponta. */
  'blade',
  /** Um anel fino e fechado. */
  'ring',
  /** Um anel com quatro cortes nos pontos cardeais. */
  'ring-broken',
  /** O selo: dois anéis e doze marcas radiais. */
  'sigil',
  /** Uma ponta de seta. */
  'arrowhead',
  /** Um ponto minúsculo e brilhante. Só resíduo. */
  'mote',
] as const;
export type PvpRuneRecipe = (typeof PVP_RUNE_RECIPES)[number];

/**
 * A banda a que uma camada pertence, que é também a sua ordenação em
 * profundidade.
 *
 * Três grupos: o selo no chão, a energia que faz o volume, e o núcleo que
 * carrega a identidade. O compositor os transforma em 7, 8 e 9 - acima do que
 * era o pad e abaixo dos obstáculos, portanto abaixo de todo casco, canhão,
 * tiro, barra e marca de equipe.
 */
export const PVP_RUNE_BANDS = ['seal', 'volume', 'core'] as const;
export type PvpRuneBand = (typeof PVP_RUNE_BANDS)[number];

/** Como uma camada respira. Limitado, e nunca rápido a ponto de piscar. */
export interface PvpRunePulse {
  readonly low: number;
  readonly high: number;
  readonly periodMs: number;
  /** Fase em voltas, para duas camadas respirarem em oposição. */
  readonly phase: number;
}

/**
 * Uma camada que circula um ponto em vez de repousar nele.
 *
 * `count` cópias igualmente distribuídas: é o mesmo mecanismo por trás das
 * cinco lâminas da Coroa e dos seis fragmentos da Singularidade.
 */
export interface PvpRuneOrbit {
  readonly count: number;
  /** Em raios de pad. */
  readonly radius: number;
  readonly periodMs: number;
  /** Se cada cópia gira para apontar para fora. */
  readonly facing: boolean;
  /** Um raio que respira, somado e subtraído ao longo do período. */
  readonly breathe: number;
}

/**
 * Uma coisa desenhada.
 *
 * Toda distância é FRAÇÃO DO RAIO DO PAD, nunca um pixel e nunca uma unidade de
 * mundo. É o que faz uma aparência sobreviver à faixa de zoom: o compositor
 * multiplica uma vez, e nada pode ser desenhado contra 1920×1080 sem querer.
 */
export interface PvpRuneLayer {
  readonly id: string;
  readonly recipe: PvpRuneRecipe;
  readonly band: PvpRuneBand;
  /** Meia-medida, em raios de pad. */
  readonly radius: number;
  readonly tint: keyof PvpRunePalette;
  readonly alpha: number;
  /** Aditivo para energia, normal para o que precisa ocluir. */
  readonly additive: boolean;
  /**
   * Rotação simulada em torno do eixo vertical: `scaleX` varre como um cosseno.
   *
   * É o truque 2.5D inteiro e custa uma multiplicação por quadro. Uma faceta
   * cuja largura vai a quase zero e volta lê como um sólido girando, coisa que
   * um sprite plano girando nunca faz. Zero desliga a varredura.
   */
  readonly sweepPeriodMs: number;
  /** Quão plano o varrido chega a ficar. Mantém uma aresta visível na virada. */
  readonly sweepFloor: number;
  /** Achatamento vertical constante: um plano visto de viés. 1 é de frente. */
  readonly squash: number;
  /** Quanto da flutuação ociosa esta camada toma. O parallax mora aqui. */
  readonly lift: number;
  /** Deslocamento fixo do centro do artefato, em raios de pad. */
  readonly offsetX: number;
  readonly offsetY: number;
  readonly pulse: PvpRunePulse | null;
  readonly orbit: PvpRuneOrbit | null;
  /** O menor LOD que ainda desenha esta camada. */
  readonly minLod: PvpRuneLod;
  /**
   * Se esta camada é FUNDAÇÃO visual em vez de decoração.
   *
   * ## O que a marcação decide, e o que ela não decide
   *
   * Nada no desenho. Ela não muda posição, escala, cor, ordem nem
   * visibilidade - é um papel declarado, e o compositor a ignora por completo.
   *
   * O que ela decide é o que uma PROVA pode exigir. A fundação de um artefato -
   * o que diz "este objeto está apoiado aqui" - tem de compartilhar o eixo do
   * núcleo e distribuir brilho simetricamente em torno dele. Um fragmento em
   * órbita, uma faísca ou uma esteira não têm essa obrigação: assimetria ali é
   * o que faz a coisa parecer viva.
   *
   * Sem a marcação, um teste de alinhamento teria de adivinhar quais pixels
   * pertencem a quê - e as lâminas de uma coroa cruzam a região do selo, o que
   * torna o palpite errado exatamente onde ele importa.
   */
  readonly structural: boolean;
}

/**
 * O selo mágico, e as duas regras que o impedem de mentir.
 *
 * Fica SOB o artefato, no pad, e é menor que o pad
 * ({@link PVP_RUNE_SEAL_MAX_RADIUS}) - um anel na borda do raio de coleta seria
 * a linguagem de uma área de efeito, e este jogo tem uma dessas. Também é
 * achatado, porque um círculo desenhado de frente numa arena vista de cima é um
 * adesivo, e um achatado é um plano.
 */
export interface PvpRuneSeal {
  /** Em raios de pad, limitado por {@link PVP_RUNE_SEAL_MAX_RADIUS}. */
  readonly radius: number;
  readonly alpha: number;
  readonly squash: number;
  readonly spinTurnsPerSecond: number;
  readonly recipe: Extract<PvpRuneRecipe, 'sigil' | 'ring' | 'ring-broken'>;
}

/** Nada desenha um selo mais largo que esta fração do pad. */
export const PVP_RUNE_SEAL_MAX_RADIUS = 0.78;

/**
 * Quão alto o artefato paira sobre o seu selo, em raios de pad.
 *
 * Uma arena vista de cima não tem perspectiva para levantar nada, então a
 * elevação precisa ser desenhada: o selo fica no centro do pad, o artefato
 * sobe um terço de raio, e o vão entre os dois é o que diz "objeto sobre uma
 * marca no chão" em vez de "adesivo". Sem ele - e o laboratório provou isso em
 * imagens - o selo vira duas lascas escapando pelas laterais do artefato.
 */
export const PVP_RUNE_HOVER_RADII = 0.34;

/** A flutuação ociosa: pequena, lenta e igual para toda camada que sobe. */
export interface PvpRuneIdle {
  /** Percurso vertical de pico, em raios de pad. */
  readonly floatRadius: number;
  readonly floatPeriodMs: number;
}

/**
 * As partículas de acabamento, com o teto declarado em vez de subentendido.
 *
 * Um emissor por FAMÍLIA - por tipo de runa - compartilhado por todo pad que
 * mostre aquela runa, e é por isso que o teto é por pad e multiplicado pelos
 * quatro pads que a arena tem.
 */
export interface PvpRuneResidue {
  /** Partículas vivas que este tipo pode manter, por pad. */
  readonly maxParticles: number;
  /** Milissegundos entre emissões, por pad. */
  readonly frequencyMs: number;
  readonly lifespanMs: number;
  /** Onde nascem, em raios de pad a partir do centro. */
  readonly spawnRadius: number;
  /** Em raios de pad por segundo; positivo afasta. */
  readonly driftPerSecond: number;
  readonly startAlpha: number;
  readonly scale: number;
}

/**
 * A coleta, nos quatro tempos.
 *
 * O total é curto de propósito. Uma runa sai quando a sala diz que saiu, e um
 * floreio longo continuaria tocando sobre um pad que já começou a regrescer -
 * por isso a sequência é limitada por {@link PVP_RUNE_COLLECT_MAX_MS}, e o
 * teste da aparência recusa um tipo que passe disso.
 */
export interface PvpRuneCollect {
  /** Tempo 1: um inchaço pequeno antes do colapso. */
  readonly anticipationMs: number;
  readonly anticipationScale: number;
  /** Tempo 2: tudo converge para o centro. */
  readonly collapseMs: number;
  /** Tempo 3: um clarão local, nunca mais largo que o pad. */
  readonly flashMs: number;
  readonly flashRadius: number;
  /** Tempo 4: o estouro de resíduo, limitado. */
  readonly burst: number;
  /** Raios de pad por segundo. */
  readonly burstSpeed: number;
}

/** Nada leva mais que isto para sair. */
export const PVP_RUNE_COLLECT_MAX_MS = 420;

/**
 * Como um tipo lê com a cor removida.
 *
 * Nomeado em vez de inferido, porque "duas runas se distinguem sem cor" é uma
 * afirmação que um teste precisa poder conferir, e ele só consegue conferir
 * contra algo escrito. Nenhum dos três se repete.
 */
export const PVP_RUNE_SILHOUETTES = ['blade-crown', 'arrowhead', 'singularity'] as const;
export type PvpRuneSilhouette = (typeof PVP_RUNE_SILHOUETTES)[number];

export interface PvpRuneAppearance {
  readonly kind: PvpRuneWireKind;
  /** O nome pelo qual a direção de arte a aprovou. */
  readonly name: string;
  readonly silhouette: PvpRuneSilhouette;
  readonly palette: PvpRunePalette;
  readonly layers: readonly PvpRuneLayer[];
  readonly seal: PvpRuneSeal;
  readonly idle: PvpRuneIdle;
  readonly residue: PvpRuneResidue;
  readonly collect: PvpRuneCollect;
}

/* -------------------------------------------------------------------------- */
/*  As três aparências                                                        */
/* -------------------------------------------------------------------------- */

/** Uma camada com tudo desligado, para que cada uma declare só o que faz. */
function layer(
  spec: Partial<PvpRuneLayer> & Pick<PvpRuneLayer, 'id' | 'recipe' | 'band'>,
): PvpRuneLayer {
  return {
    radius: 0.4,
    tint: 'body',
    alpha: 1,
    additive: false,
    sweepPeriodMs: 0,
    sweepFloor: 0.2,
    squash: 1,
    lift: 1,
    offsetX: 0,
    offsetY: 0,
    pulse: null,
    orbit: null,
    minLod: 'full',
    structural: false,
    ...spec,
  };
}

/**
 * Dano Dobrado — Coroa Incandescente.
 *
 * Um reator esférico dentro de cinco lâminas que varrem como uma coroa girando.
 * O pulso é lento e pesado (1,15 s) e não há clarão nenhum enquanto a runa está
 * parada: o que a separa de uma explosão é justamente a ausência de evento - ela
 * respira, não estoura.
 */
const DOUBLE_DAMAGE: PvpRuneAppearance = {
  kind: 'double_damage',
  name: 'Coroa Incandescente',
  silhouette: 'blade-crown',
  palette: PVP_RUNE_PALETTES.double_damage,
  layers: [
    layer({
      id: 'halo',
      recipe: 'core-glow',
      band: 'volume',
      radius: 0.58,
      tint: 'deep',
      alpha: 0.5,
      lift: 0.5,
      minLod: 'reduced',
    }),
    /*
     * A coroa: cinco lâminas, voltadas para fora, varrendo como uma só.
     *
     * A varredura é o que a faz coroa e não estrela - as lâminas do lado de lá
     * estreitam enquanto as de cá alargam, que é um sólido girando.
     */
    layer({
      id: 'crown',
      recipe: 'blade',
      band: 'volume',
      radius: 0.34,
      tint: 'body',
      alpha: 0.92,
      lift: 0.9,
      sweepPeriodMs: 3000,
      sweepFloor: 0.3,
      orbit: { count: 5, radius: 0.42, periodMs: 6400, facing: true, breathe: 0.05 },
      structural: true,
    }),
    layer({
      id: 'reactor-shell',
      recipe: 'orb-shaded',
      band: 'volume',
      radius: 0.36,
      tint: 'hot',
      alpha: 0.9,
      lift: 1,
      structural: true,
      minLod: 'reduced',
    }),
    layer({
      id: 'core',
      recipe: 'core-glow',
      band: 'core',
      radius: 0.22,
      tint: 'core',
      alpha: 1,
      additive: true,
      lift: 1.1,
      pulse: { low: 0.68, high: 1, periodMs: 1150, phase: 0 },
      structural: true,
      minLod: 'critical',
    }),
  ],
  seal: { radius: 0.74, alpha: 0.58, squash: 0.4, spinTurnsPerSecond: -0.04, recipe: 'ring' },
  idle: { floatRadius: 0.07, floatPeriodMs: 2200 },
  residue: {
    maxParticles: 8,
    frequencyMs: 230,
    lifespanMs: 780,
    spawnRadius: 0.44,
    driftPerSecond: 0.18,
    startAlpha: 0.46,
    scale: 0.46,
  },
  collect: {
    anticipationMs: 80,
    anticipationScale: 1.2,
    collapseMs: 120,
    flashMs: 100,
    flashRadius: 0.44,
    burst: 16,
    burstSpeed: 1.7,
  },
};

/**
 * Velocidade — Ponta Vetorial.
 *
 * Três cópias da mesma ponta de seta em profundidades diferentes, separadas
 * pelo parallax da flutuação, com uma lâmina curta atrás. A esteira é CURTA de
 * propósito: uma trilha longa num artefato parado no pad seria movimento que a
 * runa não está fazendo.
 */
const SPEED: PvpRuneAppearance = {
  kind: 'speed',
  name: 'Ponta Vetorial',
  silhouette: 'arrowhead',
  palette: PVP_RUNE_PALETTES.speed,
  layers: [
    layer({
      id: 'halo',
      recipe: 'core-glow',
      band: 'volume',
      radius: 0.55,
      tint: 'body',
      alpha: 0.28,
      additive: true,
      lift: 0.6,
      minLod: 'reduced',
    }),
    layer({
      id: 'wake-far',
      recipe: 'blade',
      band: 'volume',
      radius: 0.2,
      tint: 'deep',
      alpha: 0.75,
      lift: 0.5,
      offsetY: 0.34,
      squash: 1.1,
      minLod: 'full',
    }),
    layer({
      id: 'head-far',
      recipe: 'arrowhead',
      band: 'volume',
      radius: 0.54,
      tint: 'deep',
      alpha: 0.8,
      lift: 0.55,
      offsetY: 0.16,
      sweepPeriodMs: 3400,
      sweepFloor: 0.55,
      structural: true,
      minLod: 'reduced',
    }),
    layer({
      id: 'head-mid',
      recipe: 'arrowhead',
      band: 'volume',
      radius: 0.58,
      tint: 'body',
      alpha: 0.92,
      lift: 0.85,
      offsetY: 0.02,
      sweepPeriodMs: 3400,
      sweepFloor: 0.55,
      structural: true,
    }),
    /*
     * A ponta de cima é o núcleo, e continua desenhada no Critical.
     *
     * É o que mantém a runa DIRECIONAL no menor tamanho: uma seta apontando
     * continua sendo uma seta a 48 px, enquanto um ponto brilhante seria só um
     * ponto - e a direção é metade do que este tipo significa.
     */
    layer({
      id: 'head-near',
      recipe: 'arrowhead',
      band: 'core',
      radius: 0.46,
      tint: 'core',
      alpha: 0.95,
      additive: true,
      lift: 1.2,
      offsetY: -0.1,
      sweepPeriodMs: 3400,
      sweepFloor: 0.55,
      pulse: { low: 0.72, high: 1, periodMs: 1100, phase: 0 },
      structural: true,
      minLod: 'critical',
    }),
  ],
  seal: { radius: 0.68, alpha: 0.56, squash: 0.38, spinTurnsPerSecond: 0.1, recipe: 'ring-broken' },
  idle: { floatRadius: 0.11, floatPeriodMs: 2100 },
  residue: {
    maxParticles: 8,
    frequencyMs: 210,
    lifespanMs: 560,
    spawnRadius: 0.3,
    driftPerSecond: 0.34,
    startAlpha: 0.4,
    scale: 0.4,
  },
  collect: {
    anticipationMs: 60,
    anticipationScale: 1.1,
    collapseMs: 110,
    flashMs: 80,
    flashRadius: 0.38,
    burst: 12,
    burstSpeed: 2,
  },
};

/**
 * Teleporte — Singularidade Prismática.
 *
 * Fragmentos caindo para dentro em torno de um núcleo ESCURO com aro branco. O
 * centro escuro é a ideia inteira: uma bola brilhante diz "há energia aqui" e um
 * buraco diz "falta espaço aqui". O aro claro é o que dá à runa o coração
 * luminoso que o contrato exige, sem transformá-la numa lâmpada.
 *
 * Nada desenha uma linha entre origem e destino: um teleporte que mostrasse o
 * seu destino estaria contando à arena para onde um jogador vai.
 */
const TELEPORT: PvpRuneAppearance = {
  kind: 'teleport',
  name: 'Singularidade Prismática',
  silhouette: 'singularity',
  palette: PVP_RUNE_PALETTES.teleport,
  layers: [
    layer({
      id: 'halo',
      recipe: 'core-glow',
      band: 'volume',
      radius: 0.64,
      tint: 'body',
      alpha: 0.34,
      additive: true,
      lift: 0.5,
      pulse: { low: 0.24, high: 0.44, periodMs: 2100, phase: 0 },
      minLod: 'reduced',
    }),
    layer({
      id: 'shards',
      recipe: 'facet-shard',
      band: 'volume',
      radius: 0.2,
      tint: 'hot',
      alpha: 0.9,
      lift: 1.15,
      sweepPeriodMs: 2200,
      sweepFloor: 0.22,
      /* `breathe` negativo: a órbita encolhe, e a convergência é a identidade. */
      orbit: { count: 6, radius: 0.46, periodMs: 4200, facing: true, breathe: -0.12 },
    }),
    /*
     * O par que sobrevive ao Critical: o aro frio e o poço escuro.
     *
     * Os dois juntos, e não só um. Um aro sozinho seria um anel; um poço
     * sozinho seria um buraco no piso. Juntos são uma singularidade, e é essa
     * leitura que o menor tamanho tem de preservar.
     */
    layer({
      id: 'rim',
      recipe: 'ring',
      band: 'core',
      radius: 0.34,
      tint: 'core',
      alpha: 1,
      additive: true,
      lift: 1,
      pulse: { low: 0.7, high: 1, periodMs: 2100, phase: 0.5 },
      structural: true,
      minLod: 'critical',
    }),
    layer({
      id: 'well',
      recipe: 'orb-shaded',
      band: 'core',
      radius: 0.2,
      tint: 'deep',
      alpha: 1,
      lift: 1,
      structural: true,
      minLod: 'critical',
    }),
  ],
  seal: { radius: 0.6, alpha: 0.64, squash: 0.44, spinTurnsPerSecond: -0.18, recipe: 'sigil' },
  idle: { floatRadius: 0.06, floatPeriodMs: 3400 },
  residue: {
    maxParticles: 12,
    frequencyMs: 170,
    lifespanMs: 780,
    spawnRadius: 0.62,
    driftPerSecond: -0.5,
    startAlpha: 0.46,
    scale: 0.38,
  },
  collect: {
    anticipationMs: 60,
    anticipationScale: 1.06,
    collapseMs: 160,
    flashMs: 90,
    flashRadius: 0.34,
    burst: 16,
    burstSpeed: 1.2,
  },
};

/**
 * A tabela, indexada pelo tipo autoritativo.
 *
 * Três entradas para três tipos. Não existe variante, não existe chave
 * `tipo:variante`, não existe seletor - as outras seis propostas do laboratório
 * V25A ficaram na preservação e nunca entraram aqui, então não há o que
 * selecionar.
 */
export const PVP_RUNE_APPEARANCES: Readonly<Record<PvpRuneWireKind, PvpRuneAppearance>> =
  Object.freeze({
    double_damage: DOUBLE_DAMAGE,
    speed: SPEED,
    teleport: TELEPORT,
  });

export function pvpRuneAppearance(kind: PvpRuneWireKind): PvpRuneAppearance {
  return PVP_RUNE_APPEARANCES[kind];
}

/* -------------------------------------------------------------------------- */
/*  A matemática de quadro, pura                                              */
/* -------------------------------------------------------------------------- */

const TAU = Math.PI * 2;

/** Uma onda de cosseno em `[low, high]`, por período e fase em voltas. */
export function pvpRuneWave(
  low: number,
  high: number,
  periodMs: number,
  phaseTurns: number,
  elapsedMs: number,
): number {
  if (periodMs <= 0) {
    return high;
  }
  const turns = elapsedMs / periodMs + phaseTurns;
  return low + ((high - low) * (1 - Math.cos(turns * TAU))) / 2;
}

/** A flutuação, em raios de pad. Negativo é para cima, como a tela conta. */
export function pvpRuneFloatOffset(idle: PvpRuneIdle, elapsedMs: number): number {
  if (idle.floatPeriodMs <= 0) {
    return 0;
  }
  return -idle.floatRadius * Math.sin((elapsedMs / idle.floatPeriodMs) * TAU);
}

/**
 * O aperto horizontal que finge um giro em torno do eixo vertical.
 *
 * Com sinal: passada a meia volta fica negativo, o que espelha o sprite - que é
 * exatamente o que um sólido faz quando a face de trás vem para a frente.
 */
export function pvpRuneSweepScale(spec: PvpRuneLayer, elapsedMs: number): number {
  if (spec.sweepPeriodMs <= 0) {
    return 1;
  }
  const raw = Math.cos((elapsedMs / spec.sweepPeriodMs) * TAU);
  const floor = Math.max(0, Math.min(1, spec.sweepFloor));
  return raw >= 0 ? Math.max(floor, raw) : Math.min(-floor, raw);
}

export function pvpRuneLayerAlpha(spec: PvpRuneLayer, elapsedMs: number): number {
  if (!spec.pulse) {
    return spec.alpha;
  }
  const pulse = spec.pulse;
  return spec.alpha * pvpRuneWave(pulse.low, pulse.high, pulse.periodMs, pulse.phase, elapsedMs);
}

/** Onde a cópia `index` de uma camada em órbita está, em raios de pad. */
export function pvpRuneOrbitPoint(
  orbit: PvpRuneOrbit,
  index: number,
  elapsedMs: number,
): { readonly x: number; readonly y: number; readonly angle: number } {
  const turns = orbit.periodMs > 0 ? elapsedMs / orbit.periodMs : 0;
  const angle = (index / Math.max(1, orbit.count) + turns) * TAU;
  const breathe =
    orbit.breathe === 0
      ? 0
      : orbit.breathe * Math.sin((elapsedMs / Math.max(1, orbit.periodMs)) * TAU);
  const radius = orbit.radius + breathe;
  return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius, angle };
}

/** O raio desenhado do selo, em raios de pad, com o teto aplicado. */
export function pvpRuneSealRadius(seal: PvpRuneSeal): number {
  return Math.min(seal.radius, PVP_RUNE_SEAL_MAX_RADIUS);
}

/** As camadas que este LOD desenha. Nunca vazio. */
export function pvpRuneLayersForLod(
  appearance: PvpRuneAppearance,
  lod: PvpRuneLod,
): readonly PvpRuneLayer[] {
  return appearance.layers.filter((spec) => pvpRuneLodAtLeast(lod, spec.minLod));
}

/**
 * O teto de resíduo neste LOD, por pad.
 *
 * `critical` não emite nada: no zoom que o produz, o artefato inteiro tem uns
 * quarenta pixels de largura, e uma partícula ali é um pixel de ruído sobre a
 * silhueta, que é a única coisa ainda fazendo trabalho.
 */
export function pvpRuneResidueCap(appearance: PvpRuneAppearance, lod: PvpRuneLod): number {
  switch (lod) {
    case 'full':
      return appearance.residue.maxParticles;
    case 'reduced':
      return Math.floor(appearance.residue.maxParticles / 2);
    case 'critical':
      return 0;
  }
}

/** O período do pulso neste LOD: mais lento é menos trabalho, nunca mais rápido. */
export function pvpRunePulseScale(lod: PvpRuneLod): number {
  return lod === 'full' ? 1 : lod === 'reduced' ? 1.6 : 2.4;
}

export function pvpRuneCollectDurationMs(collect: PvpRuneCollect): number {
  return collect.anticipationMs + collect.collapseMs + collect.flashMs;
}

/* -------------------------------------------------------------------------- */
/*  LOD, a partir do tamanho desenhado                                        */
/* -------------------------------------------------------------------------- */

/**
 * Onde a escada troca de degrau, medido e não chutado.
 *
 * O pad tem 120 unidades de mundo de diâmetro, então o tamanho desenhado é
 * `120 × zoom`: 48 px no piso de 0,4, 90 px no padrão de 0,75 e 192 px no teto
 * de 1,6. Um artefato inteiro dentro de 48 px não carrega seis fragmentos em
 * órbita e um resíduo - por isso a escada é expressa em PIXELS DESENHADOS, e o
 * zoom é só como se chega a eles.
 */
export const PVP_RUNE_LOD_FULL_PX = 78;
/**
 * 60, e o número veio de uma medição.
 *
 * Foi 54 primeiro, que fica 6 px abaixo dos 48 px que o zoom mínimo realmente
 * desenha - perto o bastante para a histerese segurar o artefato em `reduced`
 * até o fim da descida, e a captura de zoom do laboratório reportou
 * `min=reduced` onde a escada prometia `critical`. Uma fronteira cuja folga
 * engole o extremo é uma fronteira que só vale na subida.
 */
export const PVP_RUNE_LOD_REDUCED_PX = 60;

/**
 * Quanto além de uma fronteira o tamanho precisa ir para o nível voltar.
 *
 * Um jogador girando a roda um passo por vez cruza a fronteira várias vezes, e
 * uma escada sem histerese acrescentaria e removeria a coroa de lâminas a cada
 * passo. Seis pixels é mais largo que um passo de 1,1 em qualquer ponto da
 * faixa, o que torna o tremeluzir impossível em vez de improvável.
 */
export const PVP_RUNE_LOD_HYSTERESIS_PX = 6;

export function pvpRunePadDiameterPx(padRadiusWorldUnits: number, zoom: number): number {
  return padRadiusWorldUnits * 2 * zoom;
}

/**
 * O nível para um tamanho desenhado, dado o nível já em vigor.
 *
 * `previous` não é otimização: é a histerese. `null` pede o nível com que um
 * artefato novo começa.
 */
export function pvpRuneLodForDiameter(diameterPx: number, previous: PvpRuneLod | null): PvpRuneLod {
  if (previous === null) {
    if (diameterPx >= PVP_RUNE_LOD_FULL_PX) return 'full';
    if (diameterPx >= PVP_RUNE_LOD_REDUCED_PX) return 'reduced';
    return 'critical';
  }
  const slack = PVP_RUNE_LOD_HYSTERESIS_PX;
  /* Subir custa `+slack`; cair só acontece `-slack` além da linha. */
  const climbFull = PVP_RUNE_LOD_FULL_PX + slack;
  const dropFull = PVP_RUNE_LOD_FULL_PX - slack;
  const climbReduced = PVP_RUNE_LOD_REDUCED_PX + slack;
  const dropReduced = PVP_RUNE_LOD_REDUCED_PX - slack;
  switch (previous) {
    case 'full':
      if (diameterPx >= dropFull) return 'full';
      return diameterPx >= dropReduced ? 'reduced' : 'critical';
    case 'reduced':
      if (diameterPx >= climbFull) return 'full';
      return diameterPx >= dropReduced ? 'reduced' : 'critical';
    case 'critical':
      if (diameterPx >= climbFull) return 'full';
      return diameterPx >= climbReduced ? 'reduced' : 'critical';
  }
}

/* -------------------------------------------------------------------------- */
/*  Variação reproduzível                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Um deslocamento de fase por pad com que dois clientes concordam.
 *
 * Quatro pads balançando em uníssono leem como uma animação tocada quatro
 * vezes, que é o indício de "isto é um sprite" em vez de "estes são objetos".
 * Então cada pad tem um deslocamento - mas DERIVADO da runa e do pad, nunca de
 * `Math.random`, nunca de um relógio de parede e nunca de um índice de roster.
 * Duas máquinas desenhando o mesmo instante desenham o mesmo quadro.
 */
export function pvpRuneVariationTurns(kind: PvpRuneWireKind, pad: number): number {
  let hash = 0x811c9dc5;
  const material = `${kind}:${pad}`;
  for (let index = 0; index < material.length; index += 1) {
    hash ^= material.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return (hash % 10_000) / 10_000;
}
